/**
 * timezone.ts (web) — canonical IANA time-zone helpers for the settings selector.
 *
 * Mirror of the backend authority (apps/api/src/lib/timezone.ts). Stored values are ALWAYS a
 * canonical IANA identifier (Asia/Kolkata, Europe/London, …) — never an abbreviation, a UTC
 * offset, or a country. The selector shows a friendly label + current offset + the identifier,
 * but the value it emits/stores is the IANA id.
 */

// Legacy IANA names some ICU builds still report → modern spelling (so we offer the current name).
const ALIAS_TO_CANONICAL: Readonly<Record<string, string>> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Europe/Kiev': 'Europe/Kyiv',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
};

/** True only for a canonical IANA identifier — rejects abbreviations, offsets, countries, customs. */
export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || tz.length === 0) return false;
  if (tz !== 'UTC' && !tz.includes('/')) return false; // rejects GMT/IST/BST/EST/PST + bare offsets
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function listTimeZoneIds(): string[] {
  const raw = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const normalized = new Set(raw.map((z) => ALIAS_TO_CANONICAL[z] ?? z));
  return [...normalized].sort();
}

/** Current UTC offset label for a zone, e.g. "GMT+05:30" / "GMT" (DST-aware). */
export function timeZoneOffsetLabel(tz: string, at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? 'GMT';
  } catch {
    return 'GMT';
  }
}

/** Current UTC offset in minutes (DST-aware), for sorting the selector by offset. */
export function timeZoneOffsetMinutes(tz: string, at: Date = new Date()): number {
  const m = /GMT([+-])(\d{2}):?(\d{2})?/.exec(timeZoneOffsetLabel(tz, at));
  if (!m) return 0;
  const sign = m[1] === '-' ? -1 : 1;
  return sign * (parseInt(m[2] ?? '0', 10) * 60 + parseInt(m[3] ?? '0', 10));
}

/** Friendly long zone name for a zone, e.g. "India Standard Time". Falls back to the id. */
export function timeZoneLongName(tz: string, at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? tz;
  } catch {
    return tz;
  }
}

export interface TimeZoneOption {
  value: string;        // the stored IANA id, e.g. "Asia/Kolkata"
  label: string;        // "(GMT+05:30) India Standard Time"
  sub: string;          // "Asia/Kolkata" (searchable + shows the identifier)
  offsetMinutes: number;
}

/**
 * Every canonical IANA zone as a selector option, sorted by current UTC offset then name.
 * label + sub together read as the spec's "(GMT+05:30) India Standard Time — Asia/Kolkata".
 */
export function listTimeZoneOptions(at: Date = new Date()): TimeZoneOption[] {
  return listTimeZoneIds()
    .map((tz) => ({
      value: tz,
      label: `(${timeZoneOffsetLabel(tz, at)}) ${timeZoneLongName(tz, at)}`,
      sub: tz,
      offsetMinutes: timeZoneOffsetMinutes(tz, at),
    }))
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));
}
